import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle,
  Users,
  Calculator,
  Calendar,
  Shield,
  Globe,
  Clock,
  Building2,
  FileText,
  Bot,
  Mail,
  Phone,
  Code,
  Server,
  Database,
  Briefcase,
  Key,
  FileCode,
  Handshake,
  Lock,
  Settings,
  CloudOff,
  HardDrive,
  Cpu,
  GitBranch,
  Layers,
  RefreshCcw,
  Headphones,
  FileCheck,
  ShieldCheck,
  Award,
  BarChart3,
  Sun,
  Moon,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogoFullHorizontal } from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useTheme } from '../contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactType, setContactType] = useState('quote');
  const [selectedTier, setSelectedTier] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    employees: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  // Fetch pricing from centralized API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing/tiers`);
        setPricingData(response.data);
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
        // Fallback to defaults if API fails
      } finally {
        setPricingLoading(false);
      }
    };
    fetchPricing();
  }, []);

  // Format price for display
  const formatPrice = (amount) => {
    return `RM ${amount.toLocaleString()}`;
  };

  // Transform API pricing to display format
  const getLicenseTiers = () => {
    if (!pricingData?.tiers) {
      // Fallback pricing if API fails
      return [
        {
          name: 'Starter',
          subtitle: 'Up to 30 Employees',
          licenseFee: 'RM 18,000',
          implementationFee: 'Included',
          annualSupport: 'RM 3,600/year',
          features: ['Complete source code transfer', 'Perpetual license', 'Self-deployment capable'],
          supportIncludes: ['Security patches', 'Bug fixes', 'Email support'],
          isPopular: false
        }
      ];
    }

    return pricingData.tiers.map(tier => ({
      name: tier.tier_name,
      subtitle: tier.max_employees === -1 ? 'Unlimited Employees' : `Up to ${tier.max_employees} Employees`,
      licenseFee: formatPrice(tier.license_fee),
      implementationFee: 'Included',
      annualSupport: `${formatPrice(tier.license_fee * tier.annual_support_percentage / 100)}/year`,
      features: tier.features,
      supportIncludes: tier.support_features,
      isPopular: tier.is_popular
    }));
  };

  const licenseTiers = getLicenseTiers();

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.company) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/acquisition`, {
        ...contactForm,
        contact_type: contactType,
        selected_tier: selectedTier
      });
      toast.success('Request received. Our enterprise team will contact you within 24 hours.');
      setShowContactModal(false);
      setContactForm({ name: '', email: '', company: '', employees: '', message: '' });
    } catch (err) {
      toast.success('Request received. We will contact you soon.');
      setShowContactModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const openContactModal = (type, tier = '') => {
    setContactType(type);
    setSelectedTier(tier);
    setShowContactModal(true);
  };

  // Product modules
  const productModules = [
    { icon: Users, title: 'Employee Management', desc: 'Complete employee lifecycle management' },
    { icon: Calculator, title: 'Payroll & Statutory', desc: 'EPF, SOCSO, EIS, PCB compliant' },
    { icon: Calendar, title: 'Leave Management', desc: 'Configurable leave policies and approvals' },
    { icon: Clock, title: 'Attendance', desc: 'Multiple check-in methods with overtime' },
    { icon: FileText, title: 'Claims & Expenses', desc: 'Digital submission and approval workflow' },
    { icon: Bot, title: 'Performance Reviews', desc: 'KPI tracking and 360 feedback' },
    { icon: FileCode, title: 'Digital Signatures', desc: 'DocuSign-style e-signatures' },
    { icon: Shield, title: 'Role-Based Access', desc: 'Granular permission controls' },
    { icon: Globe, title: 'Multi-Language', desc: 'English and Bahasa Malaysia' },
    { icon: FileCheck, title: 'EA Form Generation', desc: 'Malaysian tax form automation' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <LogoFullHorizontal className="h-8" />
            <div className="flex items-center space-x-4">
              <a href="#licensing" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors px-3 py-2 text-sm">
                Licensing
              </a>
              <a href="#support" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors px-3 py-2 text-sm">
                Support Model
              </a>
              <a href="#deployment" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors px-3 py-2 text-sm">
                Deployment
              </a>
              <a href="#pricing" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors px-3 py-2 text-sm">
                Pricing
              </a>
              <LanguageSelector variant="landing" />
              {/* Theme toggle (light/dark) */}
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                data-testid="theme-toggle"
                className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Button 
                onClick={() => openContactModal('evaluation')} 
                variant="outline" 
                className="border-slate-400 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:text-white hover:border-slate-400 text-sm"
              >
                Request Evaluation
              </Button>
              <Button 
                onClick={() => openContactModal('quote')} 
                className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white text-sm"
              >
                Request License Quote
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Enterprise PSCL */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-slate-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative grid lg:grid-cols-12 gap-12 items-center">
          {/* LEFT: copy + CTAs */}
          <div className="lg:col-span-7">
            {/* PSCL Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 text-sm font-medium mb-8">
              <Key className="h-4 w-4 mr-2 text-emerald-500" />
              Perpetual Source Code License (PSCL) + Optional Vendor Support
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
              Enterprise HR Management System
              <span className="block text-emerald-500 mt-2">
                Full Source Code Ownership
              </span>
            </h1>

            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl leading-relaxed">
              Acquire perpetual rights to a complete Human Resource Management System.
              Deploy on your infrastructure. Modify without restriction.
              No recurring license fees. <strong className="text-slate-900 dark:text-white">Your code. Your data. Your control.</strong>
            </p>

            {/* Key Differentiators */}
            <div className="flex flex-wrap gap-3 text-sm text-slate-500 mb-10">
              <span className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/50 px-4 py-2 rounded-lg">
                <Code className="h-4 w-4 text-emerald-500" /> Full Source Code Transfer
              </span>
              <span className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/50 px-4 py-2 rounded-lg">
                <Lock className="h-4 w-4 text-emerald-500" /> Perpetual License
              </span>
              <span className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/50 px-4 py-2 rounded-lg">
                <Server className="h-4 w-4 text-emerald-500" /> Client-Controlled Deployment
              </span>
              <span className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/50 px-4 py-2 rounded-lg">
                <Shield className="h-4 w-4 text-emerald-500" /> Malaysia Statutory Compliant
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                onClick={() => openContactModal('quote')}
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white text-base h-12 px-7"
                data-testid="cta-request-quote"
              >
                <Handshake className="h-5 w-5 mr-2" />
                Request License Quote
              </Button>
              <Button
                onClick={() => openContactModal('evaluation')}
                size="lg"
                variant="outline"
                className="text-base h-12 px-7 border-slate-400 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-slate-800"
                data-testid="cta-request-evaluation"
              >
                <FileCheck className="h-5 w-5 mr-2" />
                Request 14-Day Evaluation
              </Button>
              <Button
                onClick={() => openContactModal('consultation')}
                size="lg"
                variant="outline"
                className="text-base h-12 px-7 border-slate-400 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-slate-800"
                data-testid="cta-schedule-consultation"
              >
                <Phone className="h-5 w-5 mr-2" />
                Schedule Consultation
              </Button>
            </div>

            <p className="text-xs text-slate-500 mt-3">
              Trusted by HR teams across SEA · Built on FastAPI + React · GDPR & PDPA aligned
            </p>
          </div>

          {/* RIGHT: enterprise imagery — composite of an HR boardroom + dashboard sliver */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] lg:aspect-[3/4] rounded-2xl overflow-hidden border border-slate-300 dark:border-slate-700 shadow-2xl shadow-emerald-900/10">
              {/* Primary hero photo: HR boardroom / professional meeting */}
              <img
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80"
                alt="HR team reviewing workforce analytics on MyHR Orbit"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {/* Dark gradient blend so it fits the slate background */}
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/85 via-slate-950/40 to-slate-900/10"></div>
              {/* Bottom-left meta card — gives "enterprise dashboard" feel */}
              <div className="absolute bottom-5 left-5 right-5 bg-slate-50 dark:bg-slate-900/80 backdrop-blur-md border border-slate-300 dark:border-slate-700/60 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="h-5 w-5 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-600 dark:text-slate-400 truncate">Live Workforce Insights</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                    11 modules · Payroll · Leave · Claims · Compliance
                  </p>
                </div>
              </div>
            </div>
            {/* Floating accent — small headshot in top-right of the photo, out of CTA path */}
            <div className="hidden lg:block absolute -top-6 -right-6 w-24 h-24 rounded-2xl overflow-hidden border border-slate-300 dark:border-slate-700 shadow-xl shadow-black/40">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80"
                alt="HR leader portrait"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* "Built for Enterprise HR Teams" — image trust section */}
      <section className="py-20 px-6 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800/60 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-emerald-500/5 blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-14">
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 text-sm font-medium mb-4">
              <Building2 className="h-4 w-4 mr-2 text-emerald-500" />
              Built for Enterprise HR Teams
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              From hiring to payroll — one auditable source of truth
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              The day-to-day work of an HR leader, modelled with the rigour an audit needs and the polish your people deserve.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1: Onboarding */}
            <div className="group rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:border-emerald-500/40 transition-all">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=900&q=80"
                  alt="HR manager welcoming new hire"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-1 bg-emerald-500/15 border border-emerald-500/30 rounded-md text-emerald-400 text-xs font-medium">
                    Talent & Onboarding
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Welcome day, every day</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Offer letters, signed contracts, statutory IDs, and IT provisioning — every new joiner enters the system with a complete, audited footprint.
                </p>
              </div>
            </div>

            {/* Card 2: Compliance & Payroll */}
            <div className="group rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:border-emerald-500/40 transition-all">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80"
                  alt="Payroll specialist reviewing compliance reports"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-1 bg-emerald-500/15 border border-emerald-500/30 rounded-md text-emerald-400 text-xs font-medium">
                    Payroll & Compliance
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Audit-ready payroll, on every run</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  EPF, SOCSO, EIS, HRDF, PCB and statutory holidays modelled to Malaysian regulation. Every paid ringgit traceable to its source.
                </p>
              </div>
            </div>

            {/* Card 3: Workforce Analytics */}
            <div className="group rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:border-emerald-500/40 transition-all">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80"
                  alt="Workforce analytics charts on monitor"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-1 bg-emerald-500/15 border border-emerald-500/30 rounded-md text-emerald-400 text-xs font-medium">
                    Workforce Analytics
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">See the workforce, not just the spreadsheet</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Dashboards that turn attendance, leave patterns, and headcount drift into the questions your board actually asks at the next review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PSCL Model Section */}
      <section id="licensing" className="py-20 px-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm font-medium mb-4">
              <Key className="h-4 w-4 mr-2" />
              Licensing Model
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Perpetual Source Code License (PSCL)
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              A one-time intellectual property transfer that grants you permanent ownership rights.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Briefcase className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">One-Time License Fee</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Single payment for perpetual rights. No recurring license costs or per-user charges.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Full Source Code Transfer</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Complete codebase delivered: frontend, backend, database schemas, and documentation.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Perpetual, Non-Expiring License</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">License never expires. System operates indefinitely regardless of support status.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Server className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Deploy on Client Infrastructure</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Install on your servers, private cloud, or air-gapped environment. Full control.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <GitBranch className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Modify and Extend Without Restriction</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Customize features, add integrations, rebrand as needed. No approval required.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheck className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No Recurring License Fees</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Zero ongoing license costs. Optional support is separate and voluntary.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Optional Vendor Support Section */}
      <section id="support" className="py-20 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center mb-12">
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=80"
                  alt="HR support specialist on a customer call"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/60 via-slate-950/10 to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md border border-slate-200/70 dark:border-slate-700/60 rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <Headphones className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Vendor-led, never mandatory</p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                      24×7 enterprise SLAs · multilingual desk
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="inline-flex items-center px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 text-sm font-medium mb-4">
                <Headphones className="h-4 w-4 mr-2 text-emerald-500" />
                Support Model
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                Optional Annual Vendor Support
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl">
                Technical support is available but never required. You control your maintenance strategy — engage us when you want a partner, walk away when you don't.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What Support Includes */}
            <div className="p-8 bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                What Annual Support Includes
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Security Patches & Updates</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Vulnerability fixes and security improvements</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Technical Assistance</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Expert guidance for issues and questions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Feature Updates</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Access to new features and improvements</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">SLA Response Times</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Tiered response guarantees based on plan</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Support Terms */}
            <div className="p-8 bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                <Settings className="h-5 w-5 text-emerald-500" />
                Support Terms
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Annual Fee: 20% of License Value</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Predictable, transparent pricing</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">No Forced Renewals</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">System continues operating without support</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Self-Maintenance Option</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Full source code enables internal maintenance</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span className="text-slate-900 dark:text-white font-medium">Reactivation Available</span>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Resume support anytime with reactivation terms</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-8 p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl max-w-3xl mx-auto text-center">
            <p className="text-slate-700 dark:text-slate-300">
              <strong className="text-slate-900 dark:text-white">Important:</strong> Your PSCL license is perpetual and independent of support status. 
              The system operates indefinitely whether you subscribe to annual support or not. 
              Support is a service choice, not a license requirement.
            </p>
          </div>
        </div>
      </section>

      {/* Deployment Model Section */}
      <section id="deployment" className="py-20 px-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center mb-12">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="inline-flex items-center px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 text-sm font-medium mb-4">
                <HardDrive className="h-4 w-4 mr-2 text-emerald-500" />
                Infrastructure
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                Deployment Model
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl">
                Client-controlled infrastructure with flexible deployment options — your data centre, your cloud, your rules.
              </p>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2 relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
                  alt="Enterprise data centre and on-premise infrastructure"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/55 via-slate-950/10 to-transparent"></div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Layers className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Single-Tenant Deployment</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Dedicated instance per client. Complete data isolation. No shared resources.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Server className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Client-Owned Infrastructure</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Deploy on your servers. Your hardware. Your data center. Full sovereignty.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Docker / On-Prem / Private Cloud</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Docker Compose ready. On-premises capable. AWS, Azure, GCP compatible.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <CloudOff className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Air-Gap Capable</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">No external dependencies required. Operates in isolated networks.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Cpu className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Client-Controlled AI Keys</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">AI features use your API keys. No data sent through vendor systems.</p>
            </div>

            <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Full Data Sovereignty</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Employee data never leaves your control. Complete regulatory compliance.</p>
            </div>
          </div>

          {/* Tech Stack */}
          <div className="mt-12 p-8 bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-xl max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['React 18', 'FastAPI (Python)', 'MongoDB', 'TailwindCSS', 'Docker', 'JWT Authentication', 'REST API', 'WebSocket'].map((tech, i) => (
                <span key={i} className="px-4 py-2 bg-slate-700/50 border border-slate-400 dark:border-slate-600/50 rounded-lg text-slate-700 dark:text-slate-300 text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Centralized from API */}
      <section id="pricing" className="py-20 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm font-medium mb-4">
              <Briefcase className="h-4 w-4 mr-2" />
              License Pricing
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              PSCL License Tiers
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              One-time all-inclusive license fee. Implementation included. Optional annual support.
            </p>
          </div>

          {pricingLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {licenseTiers.map((tier, index) => (
              <div 
                key={index}
                className={`p-6 bg-slate-100 dark:bg-slate-800/50 border rounded-xl flex flex-col relative ${
                  tier.isPopular 
                    ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
                    : 'border-slate-300 dark:border-slate-700/50'
                }`}
              >
                {tier.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-slate-900 dark:text-white text-xs font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">{tier.name}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{tier.subtitle}</p>
                </div>

                {/* Pricing Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">All-Inclusive License</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">{tier.licenseFee}</div>
                  </div>
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                    <div className="text-xs text-emerald-400 mb-1">Implementation & Setup</div>
                    <div className="text-lg font-semibold text-emerald-400">Included</div>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-dashed border-slate-400 dark:border-slate-600">
                    <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Annual Support (Optional)</div>
                    <div className="text-base font-semibold text-slate-700 dark:text-slate-300">{tier.annualSupport}</div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8 flex-1">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">License Includes</h4>
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 dark:text-slate-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Support Includes */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">Support Includes (If Subscribed)</h4>
                  <ul className="space-y-2">
                    {tier.supportIncludes.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-slate-600 dark:text-slate-400 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button 
                  onClick={() => openContactModal('quote', tier.name)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white"
                >
                  Request {tier.name} Quote
                </Button>
              </div>
            ))}
          </div>
          )}

          {/* Pricing Notes */}
          <div className="mt-12 p-6 bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-xl max-w-3xl mx-auto">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Pricing Notes</h4>
            <ul className="space-y-2 text-slate-600 dark:text-slate-400 text-sm">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>All prices in Malaysian Ringgit (RM). Implementation, setup, and training INCLUDED.</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Annual support is 100% optional and can be added or discontinued at any time.</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Custom development quoted separately at RM 300/hour if needed.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Product Modules */}
      <section className="py-20 px-6 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Included Modules
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Complete HR management system with Malaysia statutory compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {productModules.map((module, index) => {
              const Icon = module.icon;
              return (
                <div key={index} className="p-5 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 rounded-xl">
                  <Icon className="h-8 w-8 text-emerald-500 mb-3" />
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1 text-sm">{module.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs">{module.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Malaysia Compliance */}
          <div className="mt-12 p-8 bg-slate-100 dark:bg-slate-800/30 border border-emerald-500/30 rounded-xl max-w-4xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center gap-4">
                <div className="text-4xl">🇲🇾</div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Malaysia Statutory Compliance</h3>
                  <p className="text-slate-600 dark:text-slate-400">EPF, SOCSO, EIS, PCB calculations with EA Form generation.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm font-medium">EPF</span>
                <span className="px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm font-medium">SOCSO</span>
                <span className="px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm font-medium">EIS</span>
                <span className="px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm font-medium">PCB</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-white dark:bg-slate-950 relative overflow-hidden">
        {/* Background imagery — modern HR boardroom, kept subtle */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=80"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-10 dark:opacity-15"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/95 to-white dark:from-slate-950/80 dark:via-slate-950/95 dark:to-slate-950"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Ready to Discuss Licensing?
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
            Our enterprise team will prepare a detailed proposal based on your organization's requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => openContactModal('quote')}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8"
            >
              <Handshake className="h-5 w-5 mr-2" />
              Request License Quote
            </Button>
            <Button 
              onClick={() => openContactModal('evaluation')}
              size="lg"
              variant="outline"
              className="border-slate-400 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 h-12 px-8"
            >
              <FileCheck className="h-5 w-5 mr-2" />
              Request Enterprise Evaluation
            </Button>
            <Button 
              onClick={() => openContactModal('consultation')}
              size="lg"
              variant="outline"
              className="border-slate-400 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 h-12 px-8"
            >
              <Phone className="h-5 w-5 mr-2" />
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <LogoFullHorizontal className="h-8" />
              <span className="text-slate-600">|</span>
              <span className="text-slate-600 dark:text-slate-400 text-sm">Perpetual Source Code License (PSCL)</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6 text-slate-600 dark:text-slate-400 text-sm">
              <a href="/license-agreement" className="hover:text-slate-900 dark:text-white flex items-center gap-2">
                <FileText className="h-4 w-4" />
                License Agreement
              </a>
              <a href="/sales-pitch" className="hover:text-slate-900 dark:text-white flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Sales Deck
              </a>
              <a href="/login" className="hover:text-slate-900 dark:text-white flex items-center gap-2">
                <Key className="h-4 w-4" />
                Client Demo Portal
              </a>
              <a href="mailto:enterprise@myhrorbit.com" className="hover:text-slate-900 dark:text-white flex items-center gap-2">
                <Mail className="h-4 w-4" />
                enterprise@myhrorbit.com
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-800 text-center text-slate-500 text-sm">
            <p>© 2026 MyHR Orbit. All rights reserved.</p>
            <p className="mt-2">Perpetual, non-exclusive source code licenses for enterprise HR management.</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {contactType === 'quote' && <><Handshake className="h-5 w-5 text-emerald-500" /> Request License Quote</>}
              {contactType === 'evaluation' && <><FileCheck className="h-5 w-5 text-emerald-500" /> Request Enterprise Evaluation</>}
              {contactType === 'consultation' && <><Phone className="h-5 w-5 text-emerald-500" /> Schedule Technical Consultation</>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedTier && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  Selected Tier: <strong>{selectedTier}</strong>
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Contact Name *</label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Business Email *</label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="name@company.com"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Organization *</label>
              <Input
                value={contactForm.company}
                onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Legal entity name"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Employee Count</label>
              <Input
                value={contactForm.employees}
                onChange={(e) => setContactForm(prev => ({ ...prev, employees: e.target.value }))}
                placeholder="Approximate number"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Requirements / Notes</label>
              <Textarea
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Deployment requirements, timeline, specific needs..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowContactModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleContactSubmit} 
              disabled={submitting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}